// src/components/DemoVideo.jsx
import { useEffect, useRef, useState } from 'react';

export default function DemoVideo({ src, poster }) {
    const videoRef = useRef(null);
    const [playing, setPlaying] = useState(true);
    const [muted, setMuted] = useState(true);
    const [volume, setVolume] = useState(1);
    const [progress, setProgress] = useState(0);
    const [seeking, setSeeking] = useState(false);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handleTimeUpdate = () => {
            if (!seeking && video.duration) {
                setProgress((video.currentTime / video.duration) * 100);
            }
        };
        const handlePlay = () => setPlaying(true);
        const handlePause = () => setPlaying(false);

        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);

        return () => {
            video.removeEventListener('timeupdate', handleTimeUpdate);
            video.removeEventListener('play', handlePlay);
            video.removeEventListener('pause', handlePause);
        };
    }, [seeking]);

    const togglePlay = () => {
        const video = videoRef.current;
        if (!video) return;
        if (video.paused) video.play();
        else video.pause();
    };

    const toggleMute = () => {
        const video = videoRef.current;
        if (!video) return;
        video.muted = !video.muted;
        setMuted(video.muted);
    };

    const handleSeek = (e) => {
        const value = Number(e.target.value);
        setProgress(value);
        const video = videoRef.current;
        if (video?.duration) {
            video.currentTime = (value / 100) * video.duration;
        }
    };

    const handleVolumeChange = (e) => {
        const value = Number(e.target.value) / 100;
        setVolume(value);
        const video = videoRef.current;
        if (!video) return;
        video.volume = value;
        if (value > 0 && muted) {
            video.muted = false;
            setMuted(false);
        }
    };

    return (
        <div className="demo-video">
            <video
                ref={videoRef}
                className="demo-video-media"
                src={src}
                poster={poster}
                autoPlay
                loop
                muted={muted}
                playsInline
                onClick={togglePlay}
            />

            <div className="demo-video-overlay">
                <button
                    type="button"
                    className="demo-video-btn"
                    onClick={togglePlay}
                    aria-label={playing ? 'Mettre en pause' : 'Lire'}
                >
                    {playing ? (
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                            <rect x="6" y="5" width="4" height="14" rx="1" />
                            <rect x="14" y="5" width="4" height="14" rx="1" />
                        </svg>
                    ) : (
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                            <path d="M7 5.5v13l12-6.5-12-6.5z" />
                        </svg>
                    )}
                </button>

                <input
                    type="range"
                    className="demo-video-progress"
                    min="0"
                    max="100"
                    step="0.1"
                    value={progress}
                    onChange={handleSeek}
                    onPointerDown={() => setSeeking(true)}
                    onPointerUp={() => setSeeking(false)}
                    aria-label="Avancement de la vidéo"
                />

                <button
                    type="button"
                    className="demo-video-btn"
                    onClick={toggleMute}
                    aria-label={muted ? 'Activer le son' : 'Couper le son'}
                >
                    {muted || volume === 0 ? (
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 9v6h4l5 5V4L8 9H4z" fill="currentColor" stroke="none" />
                            <line x1="16" y1="9" x2="21" y2="15" />
                            <line x1="21" y1="9" x2="16" y2="15" />
                        </svg>
                    ) : (
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 9v6h4l5 5V4L8 9H4z" fill="currentColor" stroke="none" />
                            <path d="M16.5 8.5a5 5 0 0 1 0 7" />
                            <path d="M18.5 6a8 8 0 0 1 0 12" />
                        </svg>
                    )}
                </button>

                <input
                    type="range"
                    className="demo-video-volume"
                    min="0"
                    max="100"
                    step="1"
                    value={muted ? 0 : volume * 100}
                    onChange={handleVolumeChange}
                    aria-label="Volume"
                />
            </div>
        </div>
    );
}
